import type { AxiosInstance } from 'axios';
import { applyEnvelopeInterceptor as applySharedEnvelopeInterceptor } from '@workpay/ds-shared/services/envelopeInterceptor';
import { broadcastSessionExpired } from '@/hooks/useAuthBroadcast';
import { translate } from '@/lib/translate';
import { useNotificationStore } from '@/stores/notificationStore';

/**
 * Request interceptor — strips Content-Type for FormData so the browser sets
 * the multipart boundary. The legacy `auth_token` localStorage Bearer header
 * is intentionally gone: the BFF is cookie-based and the SPA never holds a token.
 */
export function applyAuthRequestInterceptor(client: AxiosInstance) {
  client.interceptors.request.use((config) => {
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  });
}

let sessionExpiryHandled = false;

/**
 * Redirect to login on an unauthorized / session-gone outcome. Guarded so a
 * burst of concurrent failing requests triggers exactly one navigation; the
 * guard self-clears after 10s so a wedged navigation cannot permanently
 * swallow a later session-expiry event.
 */
export function handleSessionExpiry() {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;
  setTimeout(() => {
    sessionExpiryHandled = false;
  }, 10_000);
  broadcastSessionExpired();
  const path = window.location.pathname + window.location.search;
  const loginUrl =
    path && path !== '/' && path !== '/login'
      ? `/login?redirect_to=${encodeURIComponent(path)}`
      : '/login';
  window.location.href = loginUrl;
}

function notifySuccess(description: string) {
  useNotificationStore.getState().addNotification({
    variant: 'success',
    title: translate(description, 'Success'),
  });
}

/**
 * Applies the shared Workpay envelope interceptor (`@workpay/ds-shared`),
 * wired with this app's side-effects: the centralised success toast and the
 * session-expiry login redirect.
 */
export function applyEnvelopeInterceptor(client: AxiosInstance) {
  applySharedEnvelopeInterceptor(client, {
    onSuccessToast: notifySuccess,
    onUnauthorized: handleSessionExpiry,
  });
}
